import { makeAutoObservable, runInAction } from 'mobx';
import type { ServiceDto, CreateServiceDto, UpdateServiceDto } from '@pro-sto/api-client';
import { 
  adminServicesGetAll, 
  adminServicesDelete,
  adminServicesCreate,
  adminServicesUpdate,
} from '@pro-sto/api-client';
import { toastStore } from './ToastStore';
import { authStore } from './AuthStore';

/**
 * Admin GET /services returns the Service entity, including service_center_uuid.
 * The generated ServiceDto omits that field.
 */
type ServiceWithCenter = ServiceDto & {
  service_center_uuid?: string;
};

/**
 * Данные формы для создания/редактирования услуги
 */
export interface ServiceFormData {
  name: string;
  description: string;
  duration: string;
  priceSedan: string;
  priceCrossover: string;
  priceSuv: string;
  priceMinivan: string;
}

/**
 * Store для управления услугами сервиса
 * Управляет загрузкой, фильтрацией и удалением услуг
 */
export class ServicesStore {
  services: ServiceDto[] = [];
  activeCategory: 'car_wash' | 'tire_service' = 'car_wash';
  isLoading = false;
  error: string | null = null;
  
  // Все категории услуг (для определения доступных вкладок переключателя)
  private allServices: ServiceDto[] = [];
  private allServicesFetchedFor: string | null = null;
  
  // Race condition protection
  private fetchRequestId = 0;
  
  // LRU кеш для услуг по категориям
  private cache = new Map<string, ServiceDto[]>();
  // Максимальный размер LRU кеша (храним 5 последних посещенных категорий)
  private readonly MAX_CACHE_SIZE = 5;
  
  // Состояние формы создания/редактирования
  formData: ServiceFormData = {
    name: '',
    description: '',
    duration: '',
    priceSedan: '',
    priceCrossover: '',
    priceSuv: '',
    priceMinivan: '',
  };
  
  formErrors: Record<string, string> = {};
  isSubmitting = false;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * API отдаёт услуги по проекту, а не по СТО. Несколько СЦ могут делить
   * один projectUuid (в т.ч. после миграции на default project) — тогда
   * в список попадают чужие доп. услуги, и создание записи падает с 400.
   */
  private filterByCurrentServiceCenter(services: ServiceDto[]): ServiceDto[] {
    const serviceCenterUuid = authStore.user?.service_center_uuid;
    if (!serviceCenterUuid) {
      return services;
    }

    return services.filter((service) => {
      const ownerUuid = (service as ServiceWithCenter).service_center_uuid;

      if (ownerUuid === serviceCenterUuid) {
        return true;
      }

      // Основные услуги без владельца — общие шаблоны проекта.
      if (!ownerUuid && service.service_type === 'main') {
        return true;
      }

      if (!ownerUuid && service.service_type === 'additional') {
        console.warn(
          '[ServicesStore] Доп. услуга без service_center_uuid исключена из списка:',
          { uuid: service.uuid, name: service.name },
        );
        return false;
      }

      return false;
    });
  }

  private getServicesCacheKey(): string {
    const serviceCenterUuid = authStore.user?.service_center_uuid ?? 'unknown';
    return `${serviceCenterUuid}_${this.activeCategory}`;
  }

  /**
   * Единоразовая загрузка всех услуг (без фильтра по категории) для определения доступных категорий
   */
  async fetchAllCategories() {
    const serviceCenterKey = authStore.user?.service_center_uuid ?? 'unknown';
    if (this.allServicesFetchedFor === serviceCenterKey) return;

    try {
      const response = await adminServicesGetAll({});
      runInAction(() => {
        this.allServices = this.filterByCurrentServiceCenter(response as ServiceDto[]);
        this.allServicesFetchedFor = serviceCenterKey;

        // Автоматически выбираем первую доступную категорию
        const available = this.availableCategories;
        if (available.length > 0 && !available.includes(this.activeCategory)) {
          this.activeCategory = available[0];
        }
      });
    } catch (error) {
      console.error('Ошибка при загрузке всех категорий:', error);
    }
  }

  /**
   * Загрузка услуг с API
   * @param options - опции загрузки
   */
  async fetchServices(options?: { silent?: boolean; forceRefresh?: boolean }) {
    const cacheKey = this.getServicesCacheKey();
    const silent = options?.silent ?? false;
    const forceRefresh = options?.forceRefresh ?? false;
    
    // Используем кеш если он есть, кроме случая принудительного обновления
    if (this.cache.has(cacheKey) && !forceRefresh) {
      this.services = this.cache.get(cacheKey)!;
      return;
    }
    
    // Race condition защита
    const currentRequestId = ++this.fetchRequestId;
    
    if (!silent) {
      this.isLoading = true;
    }
    this.error = null;

    try {
      const response = await adminServicesGetAll({
        businessType: this.activeCategory,
      });

      // Проверяем, не устарел ли запрос
      if (currentRequestId !== this.fetchRequestId) {
        return; // Игнорируем устаревший ответ
      }

      runInAction(() => {
        this.services = this.filterByCurrentServiceCenter(response as ServiceDto[]);
        this.isLoading = false;
        
        // LRU кеш: удаляем самый старый элемент если превышен лимит
        if (this.cache.size >= this.MAX_CACHE_SIZE) {
          const firstKey = this.cache.keys().next().value;
          this.cache.delete(firstKey!);
        }
        this.cache.set(cacheKey, this.services);
      });
    } catch (error) {
      // Проверяем, не устарел ли запрос
      if (currentRequestId !== this.fetchRequestId) {
        return;
      }
      
      runInAction(() => {
        this.error = 'Ошибка загрузки услуг';
        this.isLoading = false;
      });
      console.error('Ошибка при загрузке услуг:', error);
      toastStore.showError('Не удалось загрузить услуги');
    }
  }

  /**
   * Установка активной категории и перезагрузка услуг
   */
  setActiveCategory(category: 'car_wash' | 'tire_service') {
    this.activeCategory = category;
    this.fetchServices();
  }

  /**
   * Удаление услуги
   */
  async deleteService(uuid: string): Promise<boolean> {
    try {
      await adminServicesDelete({ uuid });

      runInAction(() => {
        this.cache.delete(this.getServicesCacheKey());
        this.services = this.services.filter((s) => s.uuid !== uuid);
      });

      toastStore.showSuccess('Услуга успешно удалена');
      return true;
    } catch (error) {
      console.error('Ошибка при удалении услуги:', error);
      toastStore.showError('Не удалось удалить услугу');
      return false;
    }
  }

  /**
   * Полный каталог услуг сервисного центра (все категории) — требует
   * предварительного вызова fetchAllCategories().
   */
  get allCategoriesServices(): ServiceDto[] {
    return this.allServices;
  }

  /**
   * Геттер для доступных категорий (на основе всех услуг сервисного центра)
   */
  get availableCategories(): Array<'car_wash' | 'tire_service'> {
    const source = this.filterByCurrentServiceCenter(
      this.allServicesFetchedFor ? this.allServices : this.services,
    );
    const categories = new Set<'car_wash' | 'tire_service'>();
    source.forEach(service => {
      categories.add(service.business_type);
    });
    return Array.from(categories);
  }

  /**
   * Геттер для основных услуг текущей категории
   */
  get mainServices(): ServiceDto[] {
    return this.filterByCurrentServiceCenter(this.services).filter(
      (s) => s.service_type === 'main' && s.business_type === this.activeCategory
    );
  }

  /**
   * Геттер для дополнительных услуг текущей категории
   */
  get additionalServices(): ServiceDto[] {
    return this.filterByCurrentServiceCenter(this.services).filter(
      (s) => s.service_type === 'additional' && s.business_type === this.activeCategory
    );
  }

  /**
   * Создание новой услуги
   */
  async createService(serviceData: CreateServiceDto): Promise<boolean> {
    try {
      const response = await adminServicesCreate({
        requestBody: serviceData,
      });

      runInAction(() => {
        this.cache.delete(this.getServicesCacheKey());
        const created = this.filterByCurrentServiceCenter([response as ServiceDto]);
        if (created.length > 0) {
          this.services.push(created[0]);
        }
      });

      toastStore.showSuccess('Услуга успешно создана');
      return true;
    } catch (error) {
      console.error('Ошибка при создании услуги:', error);
      toastStore.showError('Не удалось создать услугу');
      return false;
    }
  }

  /**
   * Обновление существующей услуги
   */
  async updateService(uuid: string, serviceData: UpdateServiceDto): Promise<boolean> {
    try {
      const response = await adminServicesUpdate({
        uuid,
        requestBody: serviceData,
      });

      runInAction(() => {
        this.cache.delete(this.getServicesCacheKey());
        const index = this.services.findIndex((s) => s.uuid === uuid);
        if (index !== -1) {
          const updated = this.filterByCurrentServiceCenter([response as ServiceDto]);
          if (updated.length > 0) {
            this.services[index] = updated[0];
          } else {
            this.services.splice(index, 1);
          }
        }
      });

      toastStore.showSuccess('Услуга успешно обновлена');
      return true;
    } catch (error) {
      console.error('Ошибка при обновлении услуги:', error);
      toastStore.showError('Не удалось обновить услугу');
      return false;
    }
  }

  /**
   * Преобразование данных услуги в формат формы
   */
  serviceToFormData(service: ServiceDto): ServiceFormData {
    const getPriceByClass = (carClass: string): string => {
      const priceItem = service.servicePrices?.find(
        (p) => p.parameter_value === carClass && p.parameter_type === 'simple_car_class'
      );
      return priceItem?.price?.toString() || '';
    };

    return {
      name: service.name || '',
      description: service.description || '',
      duration: service.duration_minutes?.toString() || '',
      priceSedan: getPriceByClass('sedan'),
      priceCrossover: getPriceByClass('crossover'),
      priceSuv: getPriceByClass('suv'),
      priceMinivan: getPriceByClass('minivan'),
    };
  }

  /**
   * Преобразование данных формы в формат API для создания/обновления
   * @param formData - данные формы
   * @param serviceType - тип услуги (основная/дополнительная)
   * @param serviceCenterUuid - UUID сервисного центра (только для создания)
   */
  formDataToServiceDto(
    formData: ServiceFormData, 
    serviceType: 'main' | 'additional',
    serviceCenterUuid?: string
  ): CreateServiceDto | UpdateServiceDto {
    const baseData = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      duration_minutes: parseInt(formData.duration, 10),
      category: 'standard',
      business_type: this.activeCategory,
      service_type: serviceType,
      servicePrices: [
        {
          parameter_type: 'simple_car_class' as const,
          parameter_value: 'sedan',
          price: parseInt(formData.priceSedan, 10),
          is_active: true,
        },
        {
          parameter_type: 'simple_car_class' as const,
          parameter_value: 'crossover',
          price: parseInt(formData.priceCrossover, 10),
          is_active: true,
        },
        {
          parameter_type: 'simple_car_class' as const,
          parameter_value: 'suv',
          price: parseInt(formData.priceSuv, 10),
          is_active: true,
        },
        {
          parameter_type: 'simple_car_class' as const,
          parameter_value: 'minivan',
          price: parseInt(formData.priceMinivan, 10),
          is_active: true,
        },
      ],
    };

    // Добавляем service_center_uuid только при создании
    if (serviceCenterUuid) {
      return {
        ...baseData,
        service_center_uuid: serviceCenterUuid,
      } as CreateServiceDto;
    }

    return baseData as UpdateServiceDto;
  }

  /**
   * Валидация данных формы
   */
  validateFormData(formData: ServiceFormData): Record<string, string> {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Название обязательно';
    }

    const duration = parseInt(formData.duration, 10);
    if (!formData.duration || isNaN(duration) || duration <= 0) {
      errors.duration = 'Укажите продолжительность (больше 0)';
    }

    // Валидация цен
    const priceFields: Array<keyof ServiceFormData> = [
      'priceSedan', 
      'priceCrossover', 
      'priceSuv', 
      'priceMinivan'
    ];
    
    priceFields.forEach((field) => {
      const price = parseInt(formData[field], 10);
      if (!formData[field] || isNaN(price) || price < 0) {
        errors[field] = 'Укажите корректную цену';
      }
    });

    return errors;
  }

  /**
   * Инициализация формы для создания новой услуги
   */
  initCreateForm() {
    this.formData = {
      name: '',
      description: '',
      duration: '',
      priceSedan: '',
      priceCrossover: '',
      priceSuv: '',
      priceMinivan: '',
    };
    this.formErrors = {};
    this.isSubmitting = false;
  }

  /**
   * Инициализация формы для редактирования услуги
   */
  initEditForm(service: ServiceDto) {
    this.formData = this.serviceToFormData(service);
    this.formErrors = {};
    this.isSubmitting = false;
  }

  /**
   * Обновление поля формы
   */
  updateFormField(field: keyof ServiceFormData, value: string) {
    this.formData[field] = value;
    
    // Очищаем ошибку при вводе (без прямой мутации)
    if (this.formErrors[field]) {
      const newErrors = { ...this.formErrors };
      delete newErrors[field];
      this.formErrors = newErrors;
    }
  }

  /**
   * Валидация текущих данных формы и установка ошибок
   */
  validateCurrentForm(): boolean {
    this.formErrors = this.validateFormData(this.formData);
    return Object.keys(this.formErrors).length === 0;
  }

  /**
   * Сброс формы
   */
  resetForm() {
    this.formData = {
      name: '',
      description: '',
      duration: '',
      priceSedan: '',
      priceCrossover: '',
      priceSuv: '',
      priceMinivan: '',
    };
    this.formErrors = {};
    this.isSubmitting = false;
  }

  /**
   * Отправка формы (создание или обновление услуги)
   */
  async submitServiceForm(
    mode: 'create' | 'edit',
    serviceCenterUuid: string,
    serviceType: 'main' | 'additional',
    serviceUuid?: string
  ): Promise<boolean> {
    if (!this.validateCurrentForm()) {
      return false;
    }

    this.isSubmitting = true;

    try {
      let success = false;
      if (mode === 'create') {
        // При создании передаём serviceCenterUuid
        const serviceData = this.formDataToServiceDto(
          this.formData,
          serviceType,
          serviceCenterUuid
        ) as CreateServiceDto;
        success = await this.createService(serviceData);
      } else if (mode === 'edit' && serviceUuid) {
        // При редактировании НЕ передаём serviceCenterUuid
        const serviceData = this.formDataToServiceDto(
          this.formData,
          serviceType
        ) as UpdateServiceDto;
        success = await this.updateService(serviceUuid, serviceData);
      }

      if (success) {
        this.resetForm();
      }

      return success;
    } catch (error) {
      console.error('Error submitting service:', error);
      return false;
    } finally {
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  }

  /**
   * Очистка кеша услуг
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Полный сброс store (для тестов и выхода)
   */
  reset() {
    this.services = [];
    this.allServices = [];
    this.allServicesFetchedFor = null;
    this.activeCategory = 'car_wash';
    this.isLoading = false;
    this.error = null;
    this.fetchRequestId = 0;
    this.cache.clear();
    this.resetForm();
  }
}

// Экспортируем singleton instance
export const servicesStore = new ServicesStore();
